import { Switch } from "@/components/ui/switch";
import type { SearchExecutionActivity } from "@/lib/search-activity";
import { cn, toTitleCase } from "@/lib/utils";
import type { CompetitorWithMetrics } from "@/types/data";
import type { ChartBrand } from "../../types";
import {
      ScanningStepIndicator,
      type CompetitorPanelOverlayStatus,
      type ScanningProgress,
} from "./scanning-step-indicator";

export const CompetitorsPanel = ({
      competitors,
      showCompetitors,
      setShowCompetitors,
      competitorsWithMetrics,
      hiddenBrands,
      hoveredBrandId = null,
      toggleBrandVisibility,
      onCompetitorHoverEnd,
      onCompetitorHoverStart,
      getBrandColor,
      activity = null,
}: {
      competitors: ChartBrand[];
      showCompetitors: boolean;
      setShowCompetitors: (show: boolean) => void;
      competitorsWithMetrics: CompetitorWithMetrics[];
      hiddenBrands: ReadonlySet<string>;
      hoveredBrandId?: string | null;
      toggleBrandVisibility: (brandId: string) => void;
      onCompetitorHoverEnd?: (brandId: string) => void;
      onCompetitorHoverStart?: (brandId: string) => void;
      getBrandColor: (brandId: string) => string;
      activity?: SearchExecutionActivity | null;
}) => {
      // Helper to get the average visibility score for a brand from metrics
      const getAverageScore = (brandId: string): number | null => {
            const metric = competitorsWithMetrics.find((c) => c.brandId === brandId);
            return metric?.visibilityScore ?? null;
      };

      const overlayStatus: CompetitorPanelOverlayStatus | null = (() => {
            if (activity?.phase === "scanning") {
                  switch (activity.progressStep) {
                        case "analyzing":
                        case "matching":
                        case "creating":
                              return activity.progressStep;
                        default:
                              return "prepping";
                  }
            }

            if (activity?.isActive) {
                  return "waiting";
            }

            return null;
      })();

      const scanningProgress: ScanningProgress | null = (() => {
            if (!activity || activity.progressCurrent === null || activity.progressTotal === null) {
                  return null;
            }

            switch (activity.progressStep) {
                  case "analyzing":
                  case "matching":
                  case "creating":
                        return {
                              current: activity.progressCurrent,
                              step: activity.progressStep,
                              total: activity.progressTotal,
                        };
                  default:
                        return null;
            }
      })();

      return (
            <div className="relative flex w-full flex-col overflow-hidden border-t lg:w-[32%] lg:shrink-0 lg:border-t-0">
                  {overlayStatus && (
                        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-background/55 backdrop-blur-[1px] rounded-lg">
                              <ScanningStepIndicator status={overlayStatus} progress={scanningProgress} />
                        </div>
                  )}
                  {competitors.length > 0 && (
                        <>
                              <div className="border-b px-3 py-3 sm:px-4">
                                    <div className="flex items-center justify-between">
                                          <div className="text-xs font-medium text-muted-foreground/70">
                                                Competitors
                                          </div>
                                          <Switch checked={showCompetitors} onCheckedChange={setShowCompetitors} />
                                    </div>
                              </div>
                              <div
                                    className="flex-1 min-h-0 overflow-hidden"
                                    data-testid="competitors-panel-list"
                              >
                                    <div className="flex min-h-full flex-col">
                                          {competitors
                                                .sort(
                                                      (a, b) =>
                                                            (getAverageScore(b.id) ?? 0) -
                                                            (getAverageScore(a.id) ?? 0),
                                                )
                                                .map((competitor) => {
                                                      const isVisible =
                                                            showCompetitors && !hiddenBrands.has(competitor.id);
                                                      const isMuted =
                                                            hoveredBrandId !== null &&
                                                            hoveredBrandId !== competitor.id;
                                                      const isHighlighted = hoveredBrandId === competitor.id;
                                                      const score = getAverageScore(competitor.id);

                                                      return (
                                                            <button
                                                                  type="button"
                                                                  key={competitor.id}
                                                                  className={cn(
                                                                        "flex w-full flex-1 basis-0 items-center gap-2.5 border-b px-3 py-3 text-left transition-[background-color,opacity] last:border-b-0 hover:bg-muted/25 sm:px-4",
                                                                        !isVisible && "opacity-40",
                                                                        isMuted && "opacity-30",
                                                                        isHighlighted && "bg-muted/15",
                                                                  )}
                                                                  data-muted={isMuted ? "true" : "false"}
                                                                  onClick={() =>
                                                                        showCompetitors &&
                                                                        toggleBrandVisibility(competitor.id)
                                                                  }
                                                                  onMouseEnter={() =>
                                                                        isVisible &&
                                                                        onCompetitorHoverStart?.(competitor.id)
                                                                  }
                                                                  onMouseLeave={() =>
                                                                        onCompetitorHoverEnd?.(competitor.id)
                                                                  }
                                                            >
                                                                  <div
                                                                        className="w-2 h-2 rounded-full shrink-0"
                                                                        style={{
                                                                              backgroundColor: getBrandColor(
                                                                                    competitor.id,
                                                                              ),
                                                                        }}
                                                                  />
                                                                  {competitor.logo && (
                                                                        <img
                                                                              src={competitor.logo}
                                                                              alt={competitor.name}
                                                                              className="w-5 h-5 rounded-md shrink-0 object-cover"
                                                                              onError={(e) => {
                                                                                    e.currentTarget.style.display =
                                                                                          "none";
                                                                              }}
                                                                        />
                                                                  )}
                                                                  <div className="flex-1 min-w-0">
                                                                        <div className="text-sm truncate text-foreground/85">
                                                                              {toTitleCase(competitor.name)}
                                                                        </div>
                                                                  </div>
                                                                  <div className="text-sm font-semibold tabular-nums text-muted-foreground shrink-0">
                                                                        {score?.toFixed(1) ?? "—"}%
                                                                  </div>
                                                            </button>
                                                      );
                                                })}
                                    </div>
                              </div>
                        </>
                  )}
            </div>
      );
};
